/**
 * Canonical risk-analysis templates for Project Onboarding (LD-selected scale).
 * Single source of truth: UI, persisted snapshot, and Review & Finalize should use these helpers.
 * Bump RISK_ONBOARDING_TEMPLATE_VERSION if mappings change so downstream can detect drift.
 */

export const RISK_ONBOARDING_TEMPLATE_VERSION = 1;

/** Impact categories — one row each in Severity Ranks. */
export const SEVERITY_CATEGORIES = [
  "Customer Impact",
  "Financial Impact",
  "Health & Safety",
  "Impact on Reputation",
  "Legal Impact",
];

/** Column headers (qualitative → numeric mapping depends on scale). */
export const SEVERITY_COLUMNS = [
  "Negligible",
  "Minor",
  "Slight",
  "Moderate",
  "Serious",
  "High",
  "Very High",
  "Significant",
  "Catastrophic",
];

/** PLITS MAS likelihood bands (column order). */
export const LIKELIHOOD_BAND_ORDER = [
  "Improbable",
  "Unlikely",
  "Probable",
  "Likely",
  "Very likely",
  "Recent",
  "Recurring",
];

/** Time / frequency hints — PLITS MAS Risk Scale spreadsheet. */
export const LIKELIHOOD_TIME_HINT = {
  Improbable: "> Last 24 months",
  Unlikely: "Between last 24–18 months",
  Probable: "Between last 18–12 months",
  Likely: "Between last 12–6 months",
  "Very likely": "Between last 6–3 months",
  Recent: "In last 3 months",
  Recurring: "Recurring / repeat pattern",
};

const LIKELIHOOD_RANK_BY_SCALE = {
  "3": [null, 1, null, 2, 3, null, null],
  "5": [1, 2, 3, 3, 4, 5, 5],
  "10": [1, 2, 4, 4, 7, 9, 10],
};

/**
 * Cell label per column for each scale (same for every category row per your spec).
 * Use "NA" for cells that do not apply on that scale.
 */
const SEVERITY_CELL_BY_SCALE = {
  "3": {
    Negligible: "NA",
    Minor: "1",
    Slight: "NA",
    Moderate: "2",
    Serious: "NA",
    High: "3",
    "Very High": "NA",
    Significant: "NA",
    Catastrophic: "NA",
  },
  "5": {
    Negligible: "1",
    Minor: "2",
    Slight: "NA",
    Moderate: "3",
    Serious: "NA",
    High: "4",
    "Very High": "5",
    Significant: "NA",
    Catastrophic: "NA",
  },
  "10": {
    Negligible: "1",
    Minor: "2",
    Slight: "3",
    Moderate: "4",
    Serious: "5",
    High: "6",
    "Very High": "7",
    Significant: "8 - 9",
    Catastrophic: "10",
  },
};

/** Product of benchmark ranks for Project Onboarding “significant risk” (Severity × Likelihood × Consequence × Control). Not used for the advisor risk-register additive score. */
export const SIGNIFICANCE_THRESHOLD_BY_SCALE = {
  "3": 32,
  "5": 108,
  "10": 448,
};

/**
 * Risk-register-only: additive score S+L+C vs these thresholds → Significant / Not significant.
 * Calibrated to ~65% of max sum per scale (max sum = 2×maxRank + 4, since C ≤ 4).
 * | Scale | max(S+L+C) | threshold (Significant if sum ≥) |
 * |-------|------------|----------------------------------|
 * | 3     | 10         | 7                                 |
 * | 5     | 14         | 10                                |
 * | 10    | 24         | 16                                |
 */
export const RISK_REGISTER_SIGNIFICANCE_SUM_THRESHOLD_BY_SCALE = {
  "3": 7,
  "5": 10,
  "10": 16,
};

export function normalizeRiskScale(scale) {
  const s = String(scale || "").trim();
  return s === "3" || s === "5" || s === "10" ? s : "5";
}

export function severityCellValue(scale, columnKey) {
  const s = normalizeRiskScale(scale);
  const row = SEVERITY_CELL_BY_SCALE[s];
  return row && columnKey in row ? row[columnKey] : "NA";
}

export function getSignificanceThreshold(scale) {
  const s = normalizeRiskScale(scale);
  return SIGNIFICANCE_THRESHOLD_BY_SCALE[s] ?? SIGNIFICANCE_THRESHOLD_BY_SCALE["5"];
}

export function getRiskRegisterSumSignificanceThreshold(scale) {
  const s = normalizeRiskScale(scale);
  return (
    RISK_REGISTER_SIGNIFICANCE_SUM_THRESHOLD_BY_SCALE[s] ??
    RISK_REGISTER_SIGNIFICANCE_SUM_THRESHOLD_BY_SCALE["5"]
  );
}

function parseCellRank(raw) {
  const t = String(raw || "").trim();
  if (t === "NA" || !t) return null;
  if (t.includes("-")) {
    const parts = t.split("-").map((x) => parseInt(x.trim(), 10));
    if (parts.every((x) => !Number.isNaN(x))) {
      return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
    }
  }
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? null : n;
}

/** Severity numeric rank → spreadsheet column title for hover. */
export function severityRankToBandLabel(scale, rank) {
  const s = normalizeRiskScale(scale);
  const maxR = slotContentMaxRank(s);
  const r = clampInt(rank, 1, maxR);
  const cellMap = SEVERITY_CELL_BY_SCALE[s];
  for (const col of SEVERITY_COLUMNS) {
    const raw = cellMap[col];
    const parsed = parseCellRank(raw);
    if (parsed == null) continue;
    if (String(raw).includes("-")) {
      const parts = String(raw)
        .split("-")
        .map((x) => parseInt(x.trim(), 10));
      if (
        parts.length === 2 &&
        !Number.isNaN(parts[0]) &&
        !Number.isNaN(parts[1]) &&
        r >= parts[0] &&
        r <= parts[1]
      )
        return col;
    } else if (parsed === r) return col;
  }
  return "-";
}

function slotContentMaxRank(s) {
  return s === "3" ? 3 : s === "10" ? 10 : 5;
}

/** Bands that map to this rank on the scale (some ranks align multiple columns, e.g. 5-point). */
export function likelihoodRanksToBands(scale, rank) {
  const s = normalizeRiskScale(scale);
  const row = LIKELIHOOD_RANK_BY_SCALE[s];
  if (!row) return [];
  const out = [];
  for (let i = 0; i < row.length; i += 1) {
    if (row[i] === rank) out.push(LIKELIHOOD_BAND_ORDER[i]);
  }
  return out;
}

export function formatLikelihoodRankTooltip(scale, rank, likelihoodBand) {
  const band = (likelihoodBand && String(likelihoodBand).trim()) || "";
  if (band) {
    const hint = LIKELIHOOD_TIME_HINT[band];
    return hint ? `${band} - ${hint}` : band;
  }
  const bands = likelihoodRanksToBands(scale, rank);
  if (bands.length === 0) return `Rank ${rank}`;
  return bands
    .map((b) => {
      const h = LIKELIHOOD_TIME_HINT[b];
      return h ? `${b} - ${h}` : b;
    })
    .join("; ");
}

export function formatSeverityRankTooltip(scale, rank, severityBands) {
  const label = severityRankToBandLabel(scale, rank);
  const lines = [`Band (averaged): ${label}`, `Rank: ${rank}`];
  if (severityBands && typeof severityBands === "object") {
    const keys = [
      ["customerImpact", "Customer impact"],
      ["financialImpact", "Financial impact"],
      ["healthSafety", "Health & safety"],
      ["impactOnReputation", "Reputation"],
      ["legalImpact", "Legal impact"],
    ];
    for (const [k, title] of keys) {
      if (severityBands[k]) lines.push(`${title}: ${severityBands[k]}`);
    }
  }
  return lines.join("\n");
}

export function formatConsequenceRankTooltip(row, rank) {
  const yLabels = [];
  if (row?.confidentialityAffected === "Y") yLabels.push("Confidentiality");
  if (row?.integrityAffected === "Y") yLabels.push("Integrity");
  if (row?.availabilityAffected === "Y") yLabels.push("Availability");
  if (row?.privacyAffected === "Y") yLabels.push("Privacy");

  const hasAnyFlag =
    row?.confidentialityAffected != null ||
    row?.integrityAffected != null ||
    row?.availabilityAffected != null ||
    row?.privacyAffected != null;
  const sum = hasAnyFlag ? yLabels.length : clampInt(rank, 0, 4);
  const lines = [
    `Overall score: ${sum} (each of Confidentiality, Integrity, Availability, Privacy is 1 if Y, 0 if N; not scaled to severity/likelihood)`,
  ];
  if (hasAnyFlag) {
    lines.push(yLabels.length ? `Dimensions with impact: ${yLabels.join(", ")}` : "Dimensions with impact: none (all N)");
    lines.push(
      `Confidentiality: ${row?.confidentialityAffected === "Y" ? "Y" : row?.confidentialityAffected === "N" ? "N" : "-"}`,
      `Integrity: ${row?.integrityAffected === "Y" ? "Y" : row?.integrityAffected === "N" ? "N" : "-"}`,
      `Availability: ${row?.availabilityAffected === "Y" ? "Y" : row?.availabilityAffected === "N" ? "N" : "-"}`,
      `Privacy: ${row?.privacyAffected === "Y" ? "Y" : row?.privacyAffected === "N" ? "N" : "-"}`,
    );
  } else {
    lines.push("CIA+P flags not stored on this row - showing stored score only.");
  }
  return lines.join("\n");
}

/** 0–4 count of CIA+P “Y” flags on row */
export function ciaConsequenceBinarySumFromRow(row) {
  return ["confidentialityAffected", "integrityAffected", "availabilityAffected", "privacyAffected"].filter(
    (k) => row[k] === "Y",
  ).length;
}

function clampInt(n, lo, hi) {
  const x = Math.round(Number(n));
  if (Number.isNaN(x)) return lo;
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Advisor risk register: additive score S + L + C (severity + likelihood ranks + CIA+P sum 0–4).
 * Control strength is not included in `riskScore` (unchanged on the row for editing).
 * Significance uses {@link getRiskRegisterSumSignificanceThreshold}, not onboarding product thresholds.
 * Matches backend `computeRiskScoreAndSignificance`.
 *
 * @param {{ severityRank?: unknown; likelihoodRank?: unknown; consequenceRank?: unknown }} row
 * @param {string} scale — `"3"` | `"5"` | `"10"`
 */
export function computeRiskRegisterScore(row, scale) {
  const s = normalizeRiskScale(scale);
  const maxR = s === "3" ? 3 : s === "10" ? 10 : 5;
  const S = clampInt(row?.severityRank ?? 3, 1, maxR);
  const L = clampInt(row?.likelihoodRank ?? 3, 1, maxR);
  const C = clampInt(row?.consequenceRank ?? 0, 0, 4);
  const riskScore = S + L + C;
  const threshold = getRiskRegisterSumSignificanceThreshold(s);
  const riskSignificanceCategory =
    riskScore >= threshold ? "Significant" : "Not significant";
  return { riskScore, riskSignificanceCategory };
}

/**
 * Persisted blob for scoping_profile_json.riskOnboarding
 * — store scale + denormalized threshold + templateVersion for audit / Review even if code constants change later.
 */
export function buildRiskOnboardingPayload(riskScale) {
  const s = normalizeRiskScale(riskScale);
  return {
    version: 1,
    templateVersion: RISK_ONBOARDING_TEMPLATE_VERSION,
    riskScale: s,
    significanceThreshold: getSignificanceThreshold(s),
    updatedAt: Date.now(),
  };
}

export function riskOnboardingSemanticKey(payload) {
  if (!payload || typeof payload !== "object") return "";
  return JSON.stringify({
    version: payload.version ?? 1,
    templateVersion: payload.templateVersion ?? RISK_ONBOARDING_TEMPLATE_VERSION,
    riskScale: normalizeRiskScale(payload.riskScale),
    significanceThreshold: payload.significanceThreshold,
  });
}
